import { useCallback, useMemo, useState } from 'react';
import { CommonProperties } from '../common/common.interfaces';
import { Button, Form, Modal, ProgressBar } from 'react-bootstrap';
import { DutyDrawbackExceptionItem, OrderItemsFlexportTableRow } from './flexport-duty-drawback.interfaces';
import { getSalesforceUrlByWindowLocation, SALESFORCE_URLS_BY_ENVIRONMENT } from '../common/common.entities';
import { DateTime } from 'luxon';
import { useLoading } from '../loading/LoadingContext';
import { apiFetch } from '../utility/fetch';
import { API_PROD_URL } from '..';
import { SalesforceUpdateRequestBody } from '../common/common.api.interfaces';
import CustomError, { CustomErrorProps } from '../common/components/CustomError';
import { notify } from '../notify/notify';
import { CheckCircleFill } from 'react-bootstrap-icons';

interface FlexportDutyDrawbackExceptionsProps extends CommonProperties {
  modalVisible: boolean
  closeModal: any
  title: string
  exceptions: DutyDrawbackExceptionItem[]
  row: OrderItemsFlexportTableRow
}

export default function FlexportDutyDrawbackExceptions(props: FlexportDutyDrawbackExceptionsProps) {
  const [changes, setChanges]: [any[], any] = useState([])
  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const [error, setError]: [CustomErrorProps, any] = useState({visible: false})

  useCallback(() => {
    setChanges([])
  }, [props.modalVisible])

  const handleQuantityChanged = useCallback((e) => {
    const { orderitemid } = e.currentTarget.dataset;
    const quantity = +e.currentTarget.value;
  
    if (Number.isNaN(quantity)) {
      return; // Ignore non-numeric input
    }
  
    setChanges((prevChanges) => {
      // Remove if quantity is 0
      if (quantity === 0) {
        return prevChanges.filter(c => c.Id !== orderitemid);
      }
  
      // Check if item already exists
      const existingIndex = prevChanges.findIndex(c => c.Id === orderitemid);
  
      if (existingIndex !== -1) {
        // Update existing item
        const updatedChanges = [...prevChanges];
        updatedChanges[existingIndex] = { ...updatedChanges[existingIndex], Quantity: quantity };
        return updatedChanges;
      } else {
        // Add new item
        return [...prevChanges, { Id: orderitemid, Quantity: quantity }];
      }
    });
  }, []);
  
  const handleExportIgnoreChecked = useCallback((e) => {
    const { orderitemid } = e.currentTarget.dataset;
    const checked = e.currentTarget.checked;
  
    let newChanges = [...changes]; // copy the current array
  
    if (checked) {
      // Add to changes (avoid duplicates)
      const exists = newChanges.some(item => item.Id === orderitemid);
      if (!exists) {
        newChanges.push({
          Id: orderitemid,
          Exclude_Drawback__c: true
        });
      }
    } else {
      // Remove from changes
      newChanges = newChanges.filter(item => item.Id !== orderitemid);
    }
  
    setChanges(newChanges);
  }, [changes]);

  const handleSaveChangesClicked = useCallback(async () => {
    if(changes.length === 0){
      setError({
        closeError: () => {setError({visible: false})},
        content: undefined,
        description: `No order items have been modified to update.`,
        title: `Order Item Update Failed`,
        visible: true
      } as CustomErrorProps)
      return
    }
    setError({visible: false})

    changeStep(`Updating Order Items`)
    changeSubStep(`Updating ${changes.length} order item(s).`)
    toggleLoading(true)
    const updateResponse = await apiFetch({
      endpoint: 'salesforceUpdate',
      method: 'PATCH',
      accessToken: props.userData.accessToken,
      baseUrlOverride: API_PROD_URL,
      body: JSON.stringify({
        idOrExternalIdField: 'Id',
        items: changes,
        sObject: 'OrderItem'
      } as SalesforceUpdateRequestBody)
    })
    if(!updateResponse.ok){
      setError({
        closeError: () => {setError({visible: false})},
        content: JSON.stringify(updateResponse.body),
        description: `Failed to update Order Items please review the error.`,
        title: `Order Item Update Failed`,
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    //update exception blob to track changes 
    const newBlob = []
    for(const ex of props.exceptions){
      const change = changes.find(c => c.Id === ex.orderItemId)
      if(change){
        ex.completed = true
      }
      newBlob.push(ex)
    }

    changeSubStep(`Updating exception tracking.`)
    const exceptionsResponse = await apiFetch({
      endpoint: 'blob',
      method: 'POST',
      accessToken: props.userData.accessToken,
      baseUrlOverride: API_PROD_URL,
      body: JSON.stringify(newBlob),
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'flexport'},
        {key: 'blobname', value: props.row.exceptionsPath}
      ]
    })
    if(!exceptionsResponse.ok){
      setError({
        closeError: () => {setError({visible: false})},
        content: JSON.stringify(exceptionsResponse.body),
        description: `Failed to save exceptions for tracking purposes.`,
        title: `Exceptions Update Failed`,
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    toggleLoading(false)
    setChanges([])
    props.closeModal()
    notify('success', 'Order Items updated!')
  }, [changes])

  const close = () => {
    setChanges([])
    props.closeModal()
  }
  
  const view = useMemo(() => {
    const total = props.exceptions?.length
    const incomplete = props.exceptions?.filter(ex => ex.completed !== true)
    const complete = props.exceptions?.filter(ex => ex.completed === true)
    const percentIncomplete = (incomplete?.length / total) * 100
    const percentComplete = (complete?.length / total) * 100

    return <Modal show={props.modalVisible} animation={false} onHide={close} className='modal-xl'>
    <Modal.Header 
      closeButton
      style={{ 
        position: 'sticky', 
        top: -1, 
        zIndex: 1050, 
        backgroundColor: 'white', 
        borderBottom: '1px solid #ddd'
      }}
    >
      <Modal.Title>
        {props.title}
      </Modal.Title>
    </Modal.Header>
      
    <Modal.Body>
      <div>
        <div style={{textAlign: 'center'}}>
          <span className='beacon-label'>{percentComplete.toFixed(2)}% COMPLETE - {complete?.length}/{total}</span>
        </div>
        <ProgressBar>
          <ProgressBar striped animated variant='success' now={percentComplete}/>
          <ProgressBar striped animated variant='warning' now={percentIncomplete}/>
        </ProgressBar>
        <span className='beacon-label'>Change the quantity or select export ignore to resolve the following exceptions:</span>
        <table key={`${props.title}-table`} className='flexport-table'>
          <thead>
            <tr className="sticky-row-2">
              <th>#</th>
              <th>ORDER ITEM</th>
              <th>ORDER #</th>
              <th>ACCOUNT</th>
              <th>SHIPPED ON</th>
              <th>QTY</th>
              <th><Button onClick={handleSaveChangesClicked} variant='success'>SAVE CHANGES</Button></th>
            </tr>
            <tr>
              <td colSpan={7}>
                <CustomError 
                  closeError={() => {setError({visible: false})}}
                  content={error?.content}
                  description={error?.description}
                  title={error?.title}
                  visible={error?.visible}
                />
              </td>
            </tr>
          </thead>
          <tbody>
            {props.exceptions?.map((ex, index) => (
              <>
                <tr key={index}>
                  <td>{index+1}</td>
                  <td><a title={ex.productTitle} target="_blank" rel="noopener noreferrer" href={`${SALESFORCE_URLS_BY_ENVIRONMENT['production']}/${ex.orderItemId}`}>{ex.productMpn}</a></td>
                  <td>{ex.orderNumber}</td>
                  <td>{ex.orderAccount}</td>
                  <td>{DateTime.fromISO(ex.shipmentSentTime).setZone('America/Chicago').toFormat('MMM dd, yyyy t')}</td>
                  <td style={{width: '100px'}}>
                  {ex.completed === true ? <CheckCircleFill color='green' size='25' /> : 
                    <Form.Control 
                    disabled={changes.some(c => c.Id === ex.orderItemId && c.Exclude_Drawback__c)} 
                    data-orderitemid={ex.orderItemId} 
                    onChange={handleQuantityChanged} 
                    type='text' 
                    defaultValue={ex.quantity}
                    />
                  }
                  </td>
                  <td>
                    {ex.completed === true ? <CheckCircleFill color='green' size='25' /> :
                    <>
                      <Form.Check 
                        disabled={changes.some(c => c.Id === ex.orderItemId && c.Quantity > 0)}
                        onChange={handleExportIgnoreChecked}
                        data-orderitemid={ex.orderItemId}
                        id={`ignore-${ex.orderItemId}-${index}`} 
                        title={'Ignore this order item when re-running the export.'}
                      />
                      <label 
                        htmlFor={`ignore-${ex.orderItemId}-${index}`} 
                        style={{ cursor: 'pointer', fontWeight: 'lighter' }}
                        title={'Ignore this order item when re-running the export.'}
                      >
                        EXPORT IGNORE
                      </label>&nbsp;
                    </>}
                  </td>
                </tr>
                {changes?.some(c => c.Id === ex.orderItemId) && 
                  <tr key={`${index}-notify`}>
                    <td colSpan={7} style={{background: 'rgba(199, 199, 199, 0.25)'}}>
                      {changes.find(c => c.Id === ex.orderItemId)?.Exclude_Drawback__c ? 
                        'Will exclude this order item on the next re-run of the export once saved.' : 
                        `Will update the order item's quantity to ${changes.find(c => c.Id === ex.orderItemId)?.Quantity} once saved.`
                      }
                    </td>
                  </tr>
                }

              </>
            ))}
          </tbody>
        </table>
      </div>
    </Modal.Body>
    <Modal.Footer>
      <Button variant='danger' onClick={close}>Close</Button>
    </Modal.Footer>
  </Modal>
  }, [props, changes, error])

  return view
}